import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RowActions } from "@/components/dashboard/RowActions";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import usePagination from "@/lib/usePagination";
import TablePagination from "@/components/dashboard/TablePagination";

const currency = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const EMPTY = { client_id: "", employee_name: "", designation: "", period: "", period_type: "monthly", gross: 0, net: 0, status: "Paid" };

export default function Payroll() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const { page, setPage, totalPages, pageItems, rangeStart, rangeEnd, total } = usePagination(rows, 20);
  const [client, setClient] = useState("all");
  const [period, setPeriod] = useState("monthly");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [f, setF] = useState(EMPTY);

  const load = async () => {
    const params = { period_type: period };
    if (isAdmin && client !== "all") params.client_id = client;
    setRows((await api.get("/payroll", { params })).data);
  };
  useEffect(() => { (async () => { setClients((await api.get("/clients")).data); })(); }, []);
  useEffect(() => { load(); }, [client, period]);

  const totals = useMemo(() => rows.reduce((a, r) => ({ gross: a.gross + r.gross, net: a.net + r.net }), { gross: 0, net: 0 }), [rows]);

  const openCreate = () => { setEditingId(null); setF({ ...EMPTY, period_type: period, client_id: clients[0]?.client_id || "" }); setOpen(true); };
  const openEdit = (r) => { setEditingId(r.record_id); setF({ ...r }); setOpen(true); };
  const save = async () => {
    if (!f.client_id || !f.period || !f.employee_name) return toast.error("Client, period and employee name are required");
    try {
      const payload = { ...f, gross: Number(f.gross), net: Number(f.net) };
      delete payload.record_id;
      delete payload.created_at;
      if (editingId) await api.put(`/payroll/${editingId}`, payload);
      else await api.post("/payroll", payload);
      toast.success(editingId ? "Record updated" : "Record added");
      setOpen(false); setEditingId(null); setF(EMPTY); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    try { await api.delete(`/payroll/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const myClientName = !isAdmin && clients[0]?.name;

  return (
    <div className="space-y-8" data-testid="payroll-page">
      <div className="flex items-end justify-between flex-wrap gap-6">
        <div>
          <div className="label-eyebrow text-navy/50">Payroll</div>
          <h1 className="serif-display text-4xl mt-2">Track records</h1>
          {myClientName && <p className="text-navy/60 mt-2" data-testid="client-scope-name">Showing records for <span className="text-navy font-semibold">{myClientName}</span></p>}
        </div>
        <div className="flex gap-3 items-center">
          {isAdmin && (
            <Select value={client} onValueChange={setClient}>
              <SelectTrigger className="w-56" data-testid="payroll-client-filter"><SelectValue placeholder="Client"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All clients</SelectItem>
                {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40" data-testid="payroll-period-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="half_yearly">Half-yearly</SelectItem>
              <SelectItem value="yearly">Yearly</SelectItem>
            </SelectContent>
          </Select>
          {isAdmin && <button className="btn-primary" onClick={openCreate} data-testid="new-payroll-btn"><Plus size={14}/> New record</button>}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-white border border-navy/10 p-6">
          <div className="label-eyebrow text-navy/50">Records</div>
          <div className="serif-display text-4xl mt-2">{rows.length}</div>
        </div>
        <div className="bg-white border border-navy/10 p-6">
          <div className="label-eyebrow text-navy/50">Gross</div>
          <div className="serif-display text-4xl mt-2">{currency(totals.gross)}</div>
        </div>
        <div className="bg-navy text-gold border border-navy p-6 grain relative overflow-hidden">
          <div className="label-eyebrow text-gold/70 relative z-10">Net disbursed</div>
          <div className="serif-display text-4xl mt-2 relative z-10">{currency(totals.net)}</div>
        </div>
      </div>

      <div className="bg-white border border-navy/10">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Period</TableHead>
              <TableHead>Client / batch</TableHead>
              <TableHead>Designation</TableHead>
              <TableHead>Gross</TableHead>
              <TableHead>Net</TableHead>
              <TableHead>Status</TableHead>
              {user.role === "admin" && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-navy/50 py-8">No records for this filter.</TableCell></TableRow>}
            {pageItems.map((r) => (
              <TableRow key={r.record_id}>
                <TableCell className="serif-display">{r.period}</TableCell>
                <TableCell>{r.employee_name}</TableCell>
                <TableCell>{r.designation}</TableCell>
                <TableCell>{currency(r.gross)}</TableCell>
                <TableCell className="text-gold serif-display">{currency(r.net)}</TableCell>
                <TableCell><Badge className={r.status === "Paid" ? "bg-navy text-gold hover:bg-navy" : "bg-gold text-white hover:bg-gold"}>{r.status}</Badge></TableCell>
                {user.role === "admin" && (
                  <TableCell>
                    <RowActions
                      onEdit={() => openEdit(r)}
                      onDelete={() => del(r.record_id)}
                      editTestid={`edit-payroll-${r.record_id}`}
                      deleteTestid={`delete-payroll-${r.record_id}`}
                      itemLabel="this record"
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination page={page} totalPages={totalPages} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} onPrev={() => setPage((p) => Math.max(1, p - 1))} onNext={() => setPage((p) => Math.min(totalPages, p + 1))} testidPrefix="payroll-table" />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="serif-display text-2xl">{editingId ? "Edit record" : "Add payroll record"}</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Client</Label>
              <Select value={f.client_id} onValueChange={(v)=>setF({...f, client_id: v})}>
                <SelectTrigger data-testid="pr-client"><SelectValue placeholder="Select client"/></SelectTrigger>
                <SelectContent>{clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Period (e.g. 2025-11 / 2025-Q4)</Label><Input value={f.period} onChange={e=>setF({...f, period: e.target.value})}/></div>
            <div>
              <Label>Period type</Label>
              <Select value={f.period_type} onValueChange={(v)=>setF({...f, period_type: v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="half_yearly">Half-yearly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={f.status} onValueChange={(v)=>setF({...f, status: v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Paid">Paid</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Processing">Processing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Employee / batch name</Label><Input value={f.employee_name} onChange={e=>setF({...f, employee_name: e.target.value})}/></div>
            <div><Label>Designation</Label><Input value={f.designation} onChange={e=>setF({...f, designation: e.target.value})}/></div>
            <div><Label>Gross (INR)</Label><Input type="number" value={f.gross} onChange={e=>setF({...f, gross: e.target.value})}/></div>
            <div><Label>Net (INR)</Label><Input type="number" value={f.net} onChange={e=>setF({...f, net: e.target.value})}/></div>
          </div>
          <DialogFooter>
            <button className="btn-outline" onClick={()=>setOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={save} data-testid="payroll-save-submit">{editingId ? "Save changes" : "Add record"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
